import { useCallback, useEffect, useState } from "react";
import { VIEWER_MODES, VIEWER_PREFERENCE_KEY } from "components/organisms/AttachmentViewer/utils";

/**
 * Persists the user's preferred attachment viewing mode ("Remember my
 * preference"). Falls back gracefully when localStorage is unavailable
 * (e.g. private browsing) so the viewer never crashes.
 *
 * @param {string} defaultMode  Mode to use when nothing is stored.
 * @returns {[string, Function]}  [mode, setMode]
 */
export default function useAttachmentViewerPreference(defaultMode = "overlay") {
    const readStored = useCallback(() => {
        try {
            const stored = window.localStorage.getItem(VIEWER_PREFERENCE_KEY);
            return VIEWER_MODES.includes(stored) ? stored : defaultMode;
        } catch {
            return defaultMode;
        }
    }, [defaultMode]);

    const [mode, setModeState] = useState(readStored);

    // Keep in sync if the preference changes in another tab.
    useEffect(() => {
        const handleStorage = (event) => {
            if (event.key === VIEWER_PREFERENCE_KEY && VIEWER_MODES.includes(event.newValue)) {
                setModeState(event.newValue);
            }
        };
        window.addEventListener("storage", handleStorage);
        return () => window.removeEventListener("storage", handleStorage);
    }, []);

    const setMode = useCallback((next, { remember = true } = {}) => {
        if (!VIEWER_MODES.includes(next)) return;
        setModeState(next);
        if (remember) {
            try {
                window.localStorage.setItem(VIEWER_PREFERENCE_KEY, next);
            } catch {
                /* ignore persistence failures */
            }
        }
    }, []);

    return [mode, setMode];
}
